export function buildAdminCoachingGuide(): string {
  return `# Vera Admin Assist — Coaching Prompt

## What you are editing
The **system instruction** for admin-only coaching when a Fides staff member uses **Vera Assist** on an investor Firestore chat. Output is **never** sent directly to the investor — it coaches the admin and provides optional draft replies they can review before sending.

**Coded production file:** \`investment-management-backend/src/services/vera-admin-chat-assist-instruction.md\`

## When it runs
- Admin opens Vera Assist on a specific investor message (including follow-ups and re-runs on earlier messages).
- Sandbox **Run Vera assist** with a simulated transcript uses the same assembly with your saved draft prompts.

## How the backend assembles the model call
1. **Intent classifier** (\`admin_assist_intent\` prompt) runs on the **target investor message** → \`ADVICE\`, \`LOCATION\`, or \`ALLOWED\`.
2. A **user message** is built containing:
   - Full chat transcript (oldest first) up to the target message
   - Intent coaching hint for the admin
   - Optional **verified Maps data** if \`LOCATION\`
   - Explicit focus on the investor's latest message text
3. **Portfolio context markdown** + FAQ + date sit in the Gemini context cache (system side), same family as investor Vera.
4. **This Coaching Prompt** is the \`systemInstruction\`.
5. If intent is not \`ADVICE\`, **Google Search is always on** for admin assist (not gated by investor global config). The **Google Search Grounding Prompt** is appended and the tool attached.
6. Model returns JSON with \`coaching\` (admin markdown notes) and \`investorResponses\` (1–3 draft replies for the admin to click into the chat input); optional \`googleSearchGrounding\` metadata for inline citations in the admin UI.

## What this prompt should cover
- Admin-facing tone: concise, actionable, compliant.
- How to reference portfolio context vs public facts.
- Escalation / compliance reminders when intent is \`ADVICE\`.
- Structure of coaching output (bullets, suggested wording, risks to avoid).
- Concise investor-facing draft replies in \`investorResponses\`, including clarifying questions only when needed.

## What this prompt does **not** control
- Intent routing → **Intent Prompt** tab.
- Search eligibility rules → **Google Search Prompt** tab (always on except \`ADVICE\`).
- Investor-visible Vera chat → **Vera Investor Chat** product tab.

## Sandbox vs production
- Preview uses GCS-stored draft prompts for the session.
- Production assist uses coded files until a proposal is merged.

---
Google Search for admin assist is **always enabled** for non-\`ADVICE\` messages (independent of investor global chat toggles).
`;
}

export function buildAdminIntentGuide(): string {
  return `# Vera Admin Assist — Intent (Classifier) Prompt

## What you are editing
Classifies the **target investor message** before admin coaching is generated. Output **exactly one word**: \`ADVICE\`, \`LOCATION\`, or \`ALLOWED\`.

**Coded production file:** \`investment-management-backend/src/services/vera-admin-chat-assist-intent-instruction.md\`

## When it runs
- Before every admin assist request and follow-up on the selected investor message.
- Runs sequentially (fast model) — admin assist latency is acceptable here unlike investor parallel path.

## How the result is used
| Output | Effect |
|--------|--------|
| \`ADVICE\` | Coaching focuses on compliance; **Google Search is disabled** for this assist call. |
| \`LOCATION\` | Loads verified Maps context into the assist user message; search may still run for public facts. |
| \`ALLOWED\` | Standard coaching path; **Google Search enabled** per grounding prompt. |

## Interaction with the other two prompts
- **Coaching Prompt** — defines how the admin-facing answer is written once intent is known.
- **Google Search Prompt** — appended to system instruction when intent ≠ \`ADVICE\`.

## Authoring tips
- Align categories with investor global intent where possible, but examples can mention admin coaching scenarios.
- Clear \`ADVICE\` examples: "Should I invest?", "Is this a good deal for me?"
- Clear \`LOCATION\` examples: suburb amenities, travel times, nearby infrastructure.

## Sandbox vs production
- Saved sandbox text overrides coded intent for preview assist runs only.

---
Admin assist search is **not** controlled by investor Global Configuration search toggles.
`;
}

export function buildAdminGroundingGuide(): string {
  return `# Vera Admin Assist — Google Search Grounding Prompt

## What you are editing
Rules appended to the **Coaching Prompt** (system instruction) when Google Search is attached for admin assist.

**Coded production file:** \`investment-management-backend/src/services/vera-google-search-grounding-instruction.md\`

## When it runs
- Intent classifier returned \`ALLOWED\` or \`LOCATION\` (never \`ADVICE\`).
- **Always on** for admin assist — not controlled by investor "Enable Vera Google Search Grounding" global toggle.
- Applies to initial assist and follow-ups on the same investor message.

## How it is applied
- \`resolveAdminAssistGoogleSearch()\` attaches search config with shared domain exclusions.
- This prompt text is appended to the system instruction; Gemini may search for factual public gaps.
- Admin UI shows inline citation chips + source popovers when grounding metadata is returned.

## What this prompt should cover
- Search **after** portfolio context and Maps data — fill factual gaps only.
- Acceptable topics (population, median prices, council zoning facts, public infrastructure).
- Forbidden uses (personal advice, speculative claims, unverified social sources — domains are also blocked globally).
- Citation discipline for admin review.

## What this prompt does **not** control
- Investor global chat search → separate file (\`vera-global-chat-google-search-grounding-instruction.md\`) and global toggles.
- Domain exclusions → Global Configuration (shared list).
- Coaching tone → **Coaching Prompt** tab.

## Sandbox vs production
- Sandbox preview uses your edited grounding text when saved to GCS.

---
Admin assist: Google Search **on** for \`ALLOWED\` and \`LOCATION\`; **off** for \`ADVICE\`.
`;
}

export function buildAdminFullPipelineGuide(): string {
  return `# Vera Admin Assist — Full Prompt Pipeline (AI Studio context)

Three editable layers for admin-only coaching on Firestore investor chats.

## Request flow (production)
\`\`\`
Admin triggers assist on investor message
    │
    ├─► [Intent Prompt] → ADVICE | LOCATION | ALLOWED
    │
    ├─► Build user message: transcript + intent hint + Maps (if LOCATION) + focus line
    │
    ├─► Portfolio context + FAQ in Gemini cache
    │
    ├─► [Coaching Prompt] → systemInstruction
    │       + [Google Search Prompt] if intent ≠ ADVICE (always for admin)
    │
    └─► Coaching suggestion + optional googleSearchGrounding for admin UI citations
\`\`\`

## The three editable prompts

### 1. Coaching Prompt
**File:** \`vera-admin-chat-assist-instruction.md\`
Admin-facing coaching style, compliance, structure.

### 2. Intent Prompt
**File:** \`vera-admin-chat-assist-intent-instruction.md\`
Classifies investor message; disables search on ADVICE; triggers Maps on LOCATION.

### 3. Google Search Grounding Prompt
**File:** \`vera-google-search-grounding-instruction.md\`
Public-web factual lookup rules when search runs (non-ADVICE only).

## Not controlled here
- Investor global Vera search toggles (admin assist search is always on except ADVICE).
- Domain exclusions → Global Configuration.

---
Copy individual tab guides when editing one prompt at a time.
`;
}
